/**
 * Mini-initialisation dialog.
 *
 * Opened from Hankkeet page when a manual Sitedrive sync detects new
 * sections / procurement packages / locations that are present in Sitedrive
 * but missing from our DB.  This dialog lets the user configure the new
 * items (template PP, responsible person, inspection flags) and kicks off
 * the `miniInitialise` background worker, then polls progress until the
 * worker finishes.
 *
 * Renders the same `ProjectColumnEditor` UI as full initialisation. Existing
 * procurement packages (those that already exist in the DB but received new
 * work sections) are shown inside the editor as read-only cards so the user
 * can still configure their newly-discovered sections.
 *
 * Inspection platform defaults are pre-filled from the same
 * `INSPECTION_DEV_DEFAULTS` used by the full-init dialog.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  AlertIcon,
  SuccessIcon,
  ChevronDownIcon,
  SpinnerIcon,
  UncheckedIcon,
} from "@/lib/icons";
import { toFinnishLabel } from "@/lib/initStepLabels";
import {
  useInitialisationProgress,
  useCancelInitialization,
} from "./hooks/useInitialiseProject";
import {
  useMiniInitialise,
  type MiniInitInput,
  type MiniInitLocationInput,
  type MiniInitProcurementPackageInput,
  type MiniInitSectionInput,
  type MiniInitSpaceInput,
  type MiniInitDeletionInput,
} from "./hooks/useMiniInit";
import {
  useProjectInitData,
  type PlotState,
  type ProcurementPackageState,
  type SchedulingSpace,
  type SchedulingTaskForImport,
  type SpaceState,
  type TemplateProcurementPackage,
  type TemplateWorkSection,
  type WorkSectionState,
} from "./hooks/useProjectInitData";
import { useProjectInitialisationData } from "./hooks/useProjectInitialisationData";
import { useProjectTaskTemplateSetId } from "./hooks/useProjectTaskTemplateSetId";
import { ProjectColumnEditor } from "./ProjectColumnEditor";
import type {
  SchedulingSyncResult,
  SchedulingNewSection,
  SchedulingNewSectionTask,
} from "@/hooks/useSchedulingSync";

// The mini-init flow does not persist section-location responsibles, so the
// responsible column has no DB-location mapping to assign against. A stable
// empty map keeps the column inert here while satisfying the required prop.
const EMPTY_SPACE_ID_TO_LOCATION_ID: Map<string, string> = new Map();

// Dev defaults for the optional inspection-platform (Congrid) integration.
// Intentionally duplicated from ProjectInitialisationDialog so both dialogs
// can evolve independently without cross-coupling.
const INSPECTION_DEV_DEFAULTS = {
  inspectionMatrixId: "27625",
  inspectionTemplateOwnerProjectId: "TbXn6WUtuoKBJLzW6H0JvEroaWVW5ccM",
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  schedulingProjectId: string | null;
  syncResult: SchedulingSyncResult | null;
}

// -- Helpers ----------------------------------------------------------------

/** Walk the parentId chain of a scheduling space until a PLOT/TONTTI is hit. */
function getPlotIdForSpace(
  spaceId: string,
  spaceById: Map<string, SchedulingSpace>,
  plotIds: Set<string>,
): string | null {
  let current = spaceId;
  const visited = new Set<string>();
  while (current) {
    if (plotIds.has(current)) return current;
    if (visited.has(current)) return null;
    visited.add(current);
    const space = spaceById.get(current);
    if (!space?.parentId) return null;
    current = space.parentId;
  }
  return null;
}

/** Minimum of non-null dates (ISO strings), or null if all are null. */
function minDate(dates: (string | null | undefined)[]): string | null {
  const defined = dates.filter((d): d is string => !!d);
  if (defined.length === 0) return null;
  return defined.reduce((a, b) => (a < b ? a : b));
}

/** Maximum of non-null dates (ISO strings), or null if all are null. */
function maxDate(dates: (string | null | undefined)[]): string | null {
  const defined = dates.filter((d): d is string => !!d);
  if (defined.length === 0) return null;
  return defined.reduce((a, b) => (a > b ? a : b));
}

/** Build a WorkSectionState from a sitedrive section's tasks. */
function buildWorkSectionState(
  section: SchedulingNewSection,
  fallbackTasks: SchedulingTaskForImport[],
  templateWorkSectionId: string | undefined,
  availableAtWs: Map<string, Set<string>>,
): WorkSectionState {
  const taskList: SchedulingNewSectionTask[] =
    section.tasks.length > 0
      ? section.tasks
      : fallbackTasks.map((t) => ({
          name: t.name,
          spaceId: t.spaceId,
          spaceName: t.spaceName,
          plannedStart: t.plannedStart,
          plannedEnd: t.plannedEnd,
        }));

  const ordered = taskList
    .map((t, i) => ({ t, i }))
    .sort((a, b) => {
      const sa = a.t.plannedStart || "";
      const sb = b.t.plannedStart || "";
      return sa === sb ? a.i - b.i : sa < sb ? -1 : 1;
    });
  const firstIdx = ordered[0]?.i;
  const lastIdx = ordered[ordered.length - 1]?.i;

  // Task names available for this work section in the selected template set.
  // Gate the default flags so we never seed (and later submit) a flag for a
  // task that doesn't exist here — those are greyed out and uncreatable.
  const wsAvail =
    (templateWorkSectionId
      ? availableAtWs.get(templateWorkSectionId)
      : undefined) ?? new Set<string>();

  const spaces: SpaceState[] = taskList.map((t, idx) => ({
    schedulingSpaceId: t.spaceId,
    schedulingTaskIds: [],
    plannedStart: t.plannedStart || "",
    plannedEnd: t.plannedEnd || "",
    includeOsakohteenTarkastus: wsAvail.has("Osakohteen tarkastus"),
    includeMalliasennustarkastus:
      idx === firstIdx && wsAvail.has("Malliasennustarkastus"),
    includeMestanVastaanotto: idx === lastIdx && wsAvail.has("Mestan vastaanotto"),
    includeTyovaiheenvastaanotto:
      idx === lastIdx && wsAvail.has("Työvaiheenvastaanotto"),
    includeLiitedokumentit: idx === firstIdx && wsAvail.has("Liitedokumentit"),
  }));

  return {
    sectionCode: section.sectionCode,
    sectionName: section.sectionName,
    templateWorkSectionId,
    spaces,
    plannedStart: minDate(spaces.map((s) => s.plannedStart || null)),
    plannedEnd: maxDate(spaces.map((s) => s.plannedEnd || null)),
    inspectorUserId: null,
    spaceResponsibles: {},
  };
}

/**
 * Build PlotState[] from a SchedulingSyncResult.
 *
 * New procurement packages from the sync are placed under the plot identified
 * by `plotSchedulingSpaceId`. Sections whose PP isn't in newProcurementPackages
 * are placed under their resolved plot (via space-parent walk) as `isExisting`
 * PP cards — these render read-only in the editor but the newly-discovered
 * work sections under them stay editable.
 *
 * Plots with no PPs are omitted. Orphans (PPs/sections with no resolvable
 * plot) are grouped under a synthetic "Tuntematon tontti" plot so they aren't
 * lost.
 */
function buildMiniInitPlots(
  syncResult: SchedulingSyncResult,
  schedulingSpaces: SchedulingSpace[],
  tasksBySectionCode: Map<string, SchedulingTaskForImport[]>,
  templatePPByCode: Map<string, TemplateProcurementPackage>,
  templateWSByCode: Map<string, TemplateWorkSection>,
  availableAtWs: Map<string, Set<string>>,
): PlotState[] {
  const plotSpaces = schedulingSpaces
    .filter((s) => s.type === "PLOT" || s.type === "TONTTI")
    .sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));
  const spaceById = new Map(schedulingSpaces.map((s) => [s.id, s]));
  const plotIds = new Set(plotSpaces.map((p) => p.id));

  // PP map keyed by (plotSchedulingSpaceId, code) so the same code in two
  // plots renders as two distinct cards.
  const makeKey = (plotSpaceId: string | null, code: string) =>
    `${plotSpaceId ?? "-"}::${code}`;

  const ppByKey = new Map<string, ProcurementPackageState>();
  const plotByKey = new Map<string, string | null>();

  // 1. Seed new PPs.
  for (const newPp of syncResult.newProcurementPackages ?? []) {
    const template = templatePPByCode.get(newPp.code);
    if (!template) continue;
    const plotSpaceId = newPp.plotSchedulingSpaceId ?? null;
    const key = makeKey(plotSpaceId, newPp.code);
    ppByKey.set(key, {
      templateProcurementPackageId: template.id,
      code: newPp.code,
      name: newPp.name,
      responsibleUserId: null,
      includeSuunnitelmiin: true,
      includeAliurakka: true,
      includeAloitusedellytykset: true,
      includeTaloudellinenLoppuselvitys: true,
      workSections: [],
      plannedStart: newPp.plannedStart ?? null,
      plannedEnd: newPp.plannedEnd ?? null,
      isExisting: false,
    });
    plotByKey.set(key, plotSpaceId);
  }

  // 2. Attach sections to PPs. If no matching PP exists yet, create an
  //    isExisting card.
  for (const section of syncResult.newSections ?? []) {
    const ppCode = section.procurementPackageCode ?? "";
    const fallbackTasks = tasksBySectionCode.get(section.sectionCode) ?? [];
    const tasks =
      section.tasks.length > 0
        ? section.tasks.map((t) => ({ spaceId: t.spaceId }))
        : fallbackTasks.map((t) => ({ spaceId: t.spaceId }));

    // Prefer the authoritative plot the backend resolved from the task's
    // location. Fall back to walking the Sitedrive space hierarchy only when
    // the backend didn't supply one (older servers / unresolvable plot).
    const firstSpaceId = tasks[0]?.spaceId;
    const sectionPlotId =
      section.plotSchedulingSpaceId ??
      (firstSpaceId ? getPlotIdForSpace(firstSpaceId, spaceById, plotIds) : null);

    // Prefer a PP card on the same plot; fall back to any matching code.
    let key = makeKey(sectionPlotId, ppCode);
    let card = ppByKey.get(key);
    if (!card) {
      const anyMatch = [...ppByKey.entries()].find(
        ([, p]) => p.code === ppCode,
      );
      if (anyMatch) {
        key = anyMatch[0];
        card = anyMatch[1];
      }
    }

    if (!card) {
      // Existing-in-DB PP — create a read-only card.
      const template = templatePPByCode.get(ppCode);
      if (!template) continue;
      card = {
        templateProcurementPackageId: template.id,
        code: ppCode,
        name: section.procurementPackageName ?? ppCode,
        responsibleUserId: null,
        includeSuunnitelmiin: false,
        includeAliurakka: false,
        includeAloitusedellytykset: false,
        includeTaloudellinenLoppuselvitys: false,
        workSections: [],
        plannedStart: null,
        plannedEnd: null,
        isExisting: true,
      };
      ppByKey.set(key, card);
      plotByKey.set(key, sectionPlotId);
    }

    const templateWs = templateWSByCode.get(section.sectionCode);
    card.workSections.push(
      buildWorkSectionState(
        section,
        fallbackTasks,
        templateWs?.id,
        availableAtWs,
      ),
    );
  }

  // 3. Group PPs by their resolved plot.
  const ppsByPlot = new Map<string, ProcurementPackageState[]>();
  for (const [key, pp] of ppByKey) {
    const plotId = plotByKey.get(key) ?? null;
    const groupKey = plotId ?? "_orphan";
    const list = ppsByPlot.get(groupKey) ?? [];
    list.push(pp);
    ppsByPlot.set(groupKey, list);
  }

  // 4. Build ordered plots; skip plots with no PPs.
  const plots: PlotState[] = [];
  for (const plotSpace of plotSpaces) {
    const pps = ppsByPlot.get(plotSpace.id);
    if (!pps || pps.length === 0) continue;
    plots.push({
      name: plotSpace.name,
      locationSchedulingSpaceId: plotSpace.id,
      procurementPackages: pps,
    });
  }

  // Orphans get a synthetic plot so they aren't dropped silently.
  const orphans = ppsByPlot.get("_orphan");
  if (orphans && orphans.length > 0) {
    plots.push({
      name: "Tuntematon tontti",
      locationSchedulingSpaceId: null,
      procurementPackages: orphans,
    });
  }

  return plots;
}

/**
 * A single opt-in deletion row. Mirrors ProjectColumnEditor's selection rows:
 * the Checkbox is pointer-events-none and the whole row handles the toggle, so
 * styling matches the rest of the editor.
 */
function DeletionRow({
  checked,
  onToggle,
  plot,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  plot?: string | null;
  children: ReactNode;
}) {
  return (
    <li
      onClick={onToggle}
      className="flex items-start justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-muted/60 cursor-pointer"
    >
      <span className="flex flex-col min-w-0">
        <span className="text-sm leading-snug">{children}</span>
        {plot && (
          <span className="text-xs text-muted-foreground">Tontti: {plot}</span>
        )}
      </span>
      <span className="flex items-center gap-2 shrink-0 mt-0.5">
        <span className="text-xs font-medium text-red-600">Hyväksy poisto</span>
        <Checkbox
          checked={checked}
          onCheckedChange={() => {}}
          className="pointer-events-none shrink-0 border-red-300 data-[state=checked]:bg-red-200 data-[state=checked]:border-red-400 data-[state=checked]:text-red-700"
        />
      </span>
    </li>
  );
}

export function MiniInitDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  schedulingProjectId,
  syncResult,
}: Props) {
  // ---- Load required template / scheduling data -------------------------
  const { taskTemplateSetId, loading: ttsLoading } =
    useProjectTaskTemplateSetId(open ? projectId : null);

  const {
    tasks,
    spaces,
    procurementPackages: templatePPs,
    templateWorkSections,
    templateTaskAvailability,
    loading: dataLoading,
    error: dataError,
  } = useProjectInitData(
    open ? schedulingProjectId : null,
    open ? taskTemplateSetId : null,
  );

  const { users } = useProjectInitialisationData();

  // ---- State ------------------------------------------------------------
  const [plots, setPlots] = useState<PlotState[]>([]);
  const [inspectionExpanded, setInspectionExpanded] = useState(false);
  const [inspectionMatrixId, setInspectionMatrixId] = useState(
    INSPECTION_DEV_DEFAULTS.inspectionMatrixId,
  );
  const [inspectionTemplateOwnerProjectId, setInspectionTemplateOwnerProjectId] =
    useState(INSPECTION_DEV_DEFAULTS.inspectionTemplateOwnerProjectId);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(null);
  const operationIdRef = useRef<string | null>(null);
  const isDirtyRef = useRef(false);

  // Confirmed deletions, keyed by `${type}:${id}`. Default empty — deletion is
  // destructive, so each row is opt-in (the inverse of new items).
  const [confirmedDeletions, setConfirmedDeletions] = useState<Set<string>>(
    new Set(),
  );
  // Controls the styled "submit with unapproved deletions" confirmation dialog.
  const [showDeletionConfirm, setShowDeletionConfirm] = useState(false);
  const toggleDeletion = (key: string) => {
    isDirtyRef.current = true;
    setConfirmedDeletions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const { miniInitialise, loading: submitting } = useMiniInitialise();
  const { cancelInitialization } = useCancelInitialization();

  const { progress, isComplete, workerError, reset: resetProgress } =
    useInitialisationProgress(
      operationId,
      isPolling,
      () => {
        setIsPolling(false);
        setTimeout(() => {
          onOpenChange(false);
        }, 2500);
      },
      (error) => {
        setSubmitError(error);
        setIsPolling(false);
      },
    );

  // ---- Lookup maps -------------------------------------------------------

  const templatePPByCode = useMemo(() => {
    const m = new Map<string, TemplateProcurementPackage>();
    for (const pp of templatePPs) m.set(pp.code, pp);
    return m;
  }, [templatePPs]);

  const templateWSByCode = useMemo(() => {
    const m = new Map<string, TemplateWorkSection>();
    for (const ws of templateWorkSections) m.set(ws.code, ws);
    return m;
  }, [templateWorkSections]);

  const tasksBySectionCode = useMemo(() => {
    const m = new Map<string, SchedulingTaskForImport[]>();
    for (const t of tasks) {
      const bucket = m.get(t.sectionCode) ?? [];
      bucket.push(t);
      m.set(t.sectionCode, bucket);
    }
    return m;
  }, [tasks]);

  // ---- Seed state when sync data arrives --------------------------------
  useEffect(() => {
    if (!open || !syncResult) return;
    if (plots.length > 0) return;
    if (templatePPs.length === 0 || templateWorkSections.length === 0) return;
    // Wait for the template-task availability query so the availability-gated
    // default flags don't all seed false.
    if (dataLoading) return;

    setPlots(
      buildMiniInitPlots(
        syncResult,
        spaces,
        tasksBySectionCode,
        templatePPByCode,
        templateWSByCode,
        templateTaskAvailability.availableAtWs,
      ),
    );
  }, [
    open,
    syncResult,
    spaces,
    tasksBySectionCode,
    templatePPByCode,
    templateWSByCode,
    templatePPs.length,
    templateWorkSections.length,
    templateTaskAvailability,
    dataLoading,
    plots.length,
  ]);

  // ---- Reset when closed ------------------------------------------------
  useEffect(() => {
    if (!open) {
      setPlots([]);
      setSubmitError(null);
      setIsPolling(false);
      setOperationId(null);
      operationIdRef.current = null;
      isDirtyRef.current = false;
      setInspectionExpanded(false);
      setInspectionMatrixId(INSPECTION_DEV_DEFAULTS.inspectionMatrixId);
      setInspectionTemplateOwnerProjectId(
        INSPECTION_DEV_DEFAULTS.inspectionTemplateOwnerProjectId,
      );
      resetProgress();
    }
  }, [open, resetProgress]);

  // ---- Derived validation state -----------------------------------------
  const newLocations = syncResult?.newLocations ?? [];

  const newPPCount = useMemo(() => {
    let count = 0;
    for (const plot of plots) {
      for (const pp of plot.procurementPackages) {
        if (!pp.isExisting) count++;
      }
    }
    return count;
  }, [plots]);

  const missingResponsibleCount = useMemo(() => {
    let count = 0;
    for (const plot of plots) {
      for (const pp of plot.procurementPackages) {
        if (!pp.isExisting && !pp.responsibleUserId) count++;
      }
    }
    return count;
  }, [plots]);

  const deletions = syncResult?.deletions ?? null;
  const deletionCount =
    (deletions?.sectionLocations.length ?? 0) +
    (deletions?.sections.length ?? 0) +
    (deletions?.locations.length ?? 0);
  const hasDeletions = deletionCount > 0;

  // All deletion row keys, and whether every one is currently confirmed.
  // Backs the "Valitse kaikki" / "Tyhjennä valinnat" toggle in the section.
  const allDeletionKeys = deletions
    ? [
        ...deletions.sectionLocations.map((sl) => `section_location:${sl.id}`),
        ...deletions.sections.map((s) => `section:${s.id}`),
        ...deletions.locations.map((l) => `location:${l.id}`),
      ]
    : [];
  const allDeletionsSelected =
    allDeletionKeys.length > 0 &&
    allDeletionKeys.every((k) => confirmedDeletions.has(k));
  const toggleAllDeletions = () => {
    isDirtyRef.current = true;
    setConfirmedDeletions((prev) => {
      if (allDeletionKeys.every((k) => prev.has(k))) {
        // Everything selected → clear only the deletion keys.
        const next = new Set(prev);
        allDeletionKeys.forEach((k) => next.delete(k));
        return next;
      }
      return new Set([...prev, ...allDeletionKeys]);
    });
  };

  const hasAnyNewItems =
    newPPCount > 0 || newLocations.length > 0 || plots.length > 0;
  const allPPsConfigured = missingResponsibleCount === 0;
  // Deletions-only syncs are submittable too (no responsible config needed).
  const canSubmit = (hasAnyNewItems || hasDeletions) && allPPsConfigured;

  // ---- Mutators ---------------------------------------------------------
  const handleUpdatePlot = (plotIndex: number, updated: PlotState) => {
    isDirtyRef.current = true;
    setPlots((prev) => prev.map((p, i) => (i === plotIndex ? updated : p)));
  };

  // ---- Submit -----------------------------------------------------------
  const performSubmit = async () => {
    setSubmitError(null);
    resetProgress();

    const opId = crypto.randomUUID();
    operationIdRef.current = opId;
    setOperationId(opId);
    setIsPolling(true);

    try {
      const locationInputs: MiniInitLocationInput[] = newLocations.map((loc) => {
        const space = spaces.find((s) => s.id === loc.spaceId);
        return {
          schedulingSpaceId: loc.spaceId,
          name: loc.spaceName || space?.name || loc.spaceId,
          parentSchedulingSpaceId: space?.parentId ?? null,
          spaceType: space?.type ?? null,
        };
      });

      // Only NEW PPs go into newProcurementPackages.
      const ppInputs: MiniInitProcurementPackageInput[] = plots.flatMap((plot) =>
        plot.procurementPackages
          .filter((pp) => !pp.isExisting)
          .map((pp) => ({
            templateProcurementPackageId: pp.templateProcurementPackageId,
            code: pp.code,
            name: pp.name ?? null,
            responsibleUserId: pp.responsibleUserId,
            plotSchedulingSpaceId: plot.locationSchedulingSpaceId ?? "",
            plannedStart: pp.plannedStart ?? null,
            plannedEnd: pp.plannedEnd ?? null,
            includeSuunnitelmiinPerehtyminen: pp.includeSuunnitelmiin,
            includeAliurakkaPerehtyminen: pp.includeAliurakka,
            includeMestanValmistelu: pp.includeAloitusedellytykset,
            includeTaloudellinenLoppuselvitys: pp.includeTaloudellinenLoppuselvitys,
          })),
      );

      // All work sections under all PPs (new or existing) go into newSections.
      // The backend uses (plotSchedulingSpaceId, procurementPackageCode) to
      // disambiguate when the same PP code exists in multiple plots.
      const sectionInputs: MiniInitSectionInput[] = plots.flatMap((plot) =>
        plot.procurementPackages.flatMap((pp) =>
          pp.workSections
            .filter((ws) => !!ws.templateWorkSectionId)
            .map((ws) => ({
              sectionCode: ws.sectionCode,
              sectionName: ws.sectionName,
              templateWorkSectionId: ws.templateWorkSectionId!,
              procurementPackageCode: pp.code,
              plotSchedulingSpaceId: plot.locationSchedulingSpaceId,
              plannedStart: ws.plannedStart ?? null,
              plannedEnd: ws.plannedEnd ?? null,
              spaces: (() => {
                // Final guard: never submit a flag for a task not available
                // in this work section, regardless of how state was set.
                const wsAvail =
                  (ws.templateWorkSectionId
                    ? templateTaskAvailability.availableAtWs.get(
                        ws.templateWorkSectionId,
                      )
                    : undefined) ?? new Set<string>();
                return ws.spaces.map(
                  (sp): MiniInitSpaceInput => ({
                    schedulingSpaceId: sp.schedulingSpaceId,
                    includeOsakohteenTarkastus:
                      sp.includeOsakohteenTarkastus &&
                      wsAvail.has("Osakohteen tarkastus"),
                    includeMalliasennustarkastus:
                      sp.includeMalliasennustarkastus &&
                      wsAvail.has("Malliasennustarkastus"),
                    includeMestanVastaanotto:
                      sp.includeMestanVastaanotto &&
                      wsAvail.has("Mestan vastaanotto"),
                    includeTyovaiheenvastaanotto:
                      sp.includeTyovaiheenvastaanotto &&
                      wsAvail.has("Työvaiheenvastaanotto"),
                    includeLiitedokumentit:
                      sp.includeLiitedokumentit &&
                      wsAvail.has("Liitedokumentit"),
                  }),
                );
              })(),
              // Staged section-location responsibles, keyed by schedulingSpaceId
              // (incl. task-less ancestor rows). Worker resolves each to a Location.
              spaceResponsibles: Object.entries(ws.spaceResponsibles)
                .filter(([, v]) => v.responsibleUserId != null)
                .map(([schedulingSpaceId, v]) => ({
                  schedulingSpaceId,
                  responsibleUserId: v.responsibleUserId!,
                })),
            })),
        ),
      );

      const deletionInputs: MiniInitDeletionInput[] = [];
      if (deletions) {
        for (const sl of deletions.sectionLocations) {
          if (confirmedDeletions.has(`section_location:${sl.id}`))
            deletionInputs.push({ type: "section_location", id: sl.id });
        }
        for (const s of deletions.sections) {
          if (confirmedDeletions.has(`section:${s.id}`))
            deletionInputs.push({ type: "section", id: s.id });
        }
        for (const l of deletions.locations) {
          if (confirmedDeletions.has(`location:${l.id}`))
            deletionInputs.push({ type: "location", id: l.id });
        }
      }

      const input: MiniInitInput = {
        projectId,
        operationId: opId,
        newLocations: locationInputs,
        newProcurementPackages: ppInputs,
        newSections: sectionInputs,
        deletions: deletionInputs,
        inspectionMatrixId: inspectionMatrixId || null,
        inspectionTemplateOwnerProjectId: inspectionTemplateOwnerProjectId || null,
      };

      const result = await miniInitialise(input);
      if (!result?.success) {
        setSubmitError(result?.message ?? "Virhe taustatyön käynnistyksessä");
        setIsPolling(false);
      }
    } catch (err: unknown) {
      setSubmitError(
        err instanceof Error ? err.message : "Virhe mini-alustuksessa",
      );
      setIsPolling(false);
    }
  };

  // Pressing Suorita: if deletions were detected but some are unapproved, ask
  // for confirmation via a styled dialog first; otherwise submit directly.
  const handleSubmit = () => {
    if (!canSubmit) return;
    if (hasDeletions && confirmedDeletions.size < deletionCount) {
      setShowDeletionConfirm(true);
      return;
    }
    void performSubmit();
  };

  const handleStop = async () => {
    if (!operationIdRef.current) return;
    try {
      await cancelInitialization(operationIdRef.current);
    } catch {
      /* ignore — we close the dialog anyway */
    }
    setIsPolling(false);
    onOpenChange(false);
  };

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && isDirtyRef.current && !submitting && !isPolling && !isComplete) {
        const ok = window.confirm(
          "Sulje ilman tallentamista? Tekemäsi valinnat menetetään.",
        );
        if (!ok) return;
      }
      onOpenChange(next);
    },
    [onOpenChange, submitting, isPolling, isComplete],
  );

  // ---- Render -----------------------------------------------------------
  const progressPercent = progress
    ? Math.round((progress.step / progress.totalSteps) * 100)
    : 0;
  const running = submitting || isPolling;
  const terminalState = isComplete || Boolean(submitError || workerError);
  const loading = ttsLoading || dataLoading;

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[1200px] h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Uudet osakohteet — {projectName}</DialogTitle>
          <DialogDescription className="text-sm">
            Sitedrivessa havaittiin uusia osakohteita, hankintapaketteja tai
            sijainteja, joita ei vielä ole hankkeessa. Valitse vastuuhenkilöt
            ja pohjat alla ja luo uudet tietueet.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <SpinnerIcon className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Ladataan pohja- ja aikatauludataa...
              </p>
            </div>
          ) : dataError ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-destructive">
              <AlertIcon className="h-8 w-8" />
              <p className="text-sm font-medium">Virhe datan lataamisessa</p>
              <p className="text-xs text-muted-foreground max-w-md text-center">
                {dataError.message || "Tarkista yhteys Sitedriven API:in"}
              </p>
            </div>
          ) : !hasAnyNewItems && !hasDeletions ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
              <p className="text-sm">Ei uusia tietueita lisättäväksi.</p>
            </div>
          ) : (
            <>
              {/* Deletions — entities removed in Sitedrive. Opt-in (default
                  unchecked) because deletion is destructive. */}
              {hasDeletions && deletions && (
                <section className="shrink-0 border-b px-1 pb-3 mb-2">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="text-sm font-semibold">
                      Sitedrivesta poistetut ({deletionCount})
                    </h3>
                    <button
                      type="button"
                      onClick={toggleAllDeletions}
                      className="shrink-0 text-xs font-medium text-red-600 hover:text-red-700 hover:underline"
                    >
                      {allDeletionsSelected ? "Tyhjennä valinnat" : "Valitse kaikki"}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Nämä kohteet on poistettu Sitedrivesta. Valitse, mitkä
                    poistetaan myös täältä. Jos poistot poistavat
                    hankintapaketin kaikki työvaiheet, myös hankintapaketti ja
                    sen tehtävät poistetaan. Poisto koskee vain tätä
                    järjestelmää — Congrid-tarkastuksia ei poisteta
                    automaattisesti.
                  </p>
                  <ul className="space-y-0.5 rounded-md border px-1.5 py-1.5 max-h-40 overflow-auto">
                    {deletions.sectionLocations.map((sl) => {
                      const key = `section_location:${sl.id}`;
                      return (
                        <DeletionRow
                          key={key}
                          checked={confirmedDeletions.has(key)}
                          onToggle={() => toggleDeletion(key)}
                          plot={sl.plotName}
                        >
                          Työvaiheen <strong>{sl.sectionCode}</strong> työt
                          poistettu sijainnista <strong>{sl.spaceName}</strong>
                        </DeletionRow>
                      );
                    })}
                    {deletions.sections.map((s) => {
                      const key = `section:${s.id}`;
                      return (
                        <DeletionRow
                          key={key}
                          checked={confirmedDeletions.has(key)}
                          onToggle={() => toggleDeletion(key)}
                          plot={s.plotName}
                        >
                          Työvaihe <strong>{s.code}</strong> poistettu kokonaan
                          — poistaa työvaiheen, sen sijaintiliitokset ja
                          tarkastustehtävät
                        </DeletionRow>
                      );
                    })}
                    {deletions.locations.map((l) => {
                      const key = `location:${l.id}`;
                      return (
                        <DeletionRow
                          key={key}
                          checked={confirmedDeletions.has(key)}
                          onToggle={() => toggleDeletion(key)}
                          plot={l.plotName}
                        >
                          Sijainti <strong>{l.name}</strong> poistettu
                          Sitedrivesta — poistaa sijainnin ja sen
                          tarkastustehtävät
                        </DeletionRow>
                      );
                    })}
                  </ul>
                </section>
              )}

              {/* New locations summary — read-only list above the editor. */}
              {newLocations.length > 0 && (
                <section className="shrink-0 border-b px-1 pb-3 mb-2">
                  <h3 className="text-sm font-semibold mb-2">
                    Uudet sijainnit ({newLocations.length})
                  </h3>
                  <ul className="text-xs text-muted-foreground space-y-1 rounded-md border bg-muted/30 px-3 py-2 max-h-24 overflow-auto">
                    {newLocations.map((loc) => (
                      <li key={loc.spaceId}>• {loc.spaceName}</li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Column editor — same UI as full init. */}
              {hasAnyNewItems && (
                <h3 className="shrink-0 text-sm font-semibold mb-1 px-1">
                  Sitedriveen lisätyt
                </h3>
              )}
              {plots.length > 0 ? (
                <div className="flex-1 min-h-0 overflow-hidden">
                  <ProjectColumnEditor
                    plots={plots}
                    users={users}
                    allSchedulingSpaces={spaces}
                    templateTaskAvailability={templateTaskAvailability}
                    onUpdatePlot={handleUpdatePlot}
                    spaceIdToLocationId={EMPTY_SPACE_ID_TO_LOCATION_ID}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
                  <p className="text-sm">
                    Ei uusia hankintapaketteja tai työvaiheita konfiguroitavaksi.
                  </p>
                </div>
              )}

              {/* Collapsible inspection-platform config below the editor. */}
              <section className="shrink-0 border-t pt-2 mt-2">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm font-medium w-full py-1"
                  onClick={() => setInspectionExpanded((v) => !v)}
                >
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${
                      inspectionExpanded ? "" : "-rotate-90"
                    }`}
                  />
                  Tarkastusalusta (valinnainen)
                </button>
                {inspectionExpanded && (
                  <div className="grid grid-cols-2 gap-3 px-1 pt-2 pb-1">
                    <div>
                      <Label className="text-xs">Tarkastusmatriisin ID</Label>
                      <Input
                        value={inspectionMatrixId}
                        onChange={(e) => setInspectionMatrixId(e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">
                        Pohjaprojektin ID (Congrid)
                      </Label>
                      <Input
                        value={inspectionTemplateOwnerProjectId}
                        onChange={(e) =>
                          setInspectionTemplateOwnerProjectId(e.target.value)
                        }
                        className="h-9"
                      />
                    </div>
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 flex-col items-stretch gap-3 sm:flex-col sm:space-x-0 border-t px-6 py-4 bg-background">
          {running || terminalState ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  {submitError || workerError ? (
                    <AlertIcon className="h-4 w-4 text-destructive shrink-0" />
                  ) : isComplete ? (
                    <SuccessIcon className="h-4 w-4 text-green-600 shrink-0" />
                  ) : (
                    <SpinnerIcon className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                  )}
                  <span className="font-medium truncate">
                    {submitError || workerError
                      ? "Synkronointi epäonnistui"
                      : isComplete
                        ? "Synkronointi valmis"
                        : progress?.stepLabel
                          ? toFinnishLabel(progress.stepLabel)
                          : "Käynnistetään taustatyötä..."}
                  </span>
                </div>
                <span className="text-muted-foreground tabular-nums shrink-0">
                  {progress && !terminalState
                    ? `Vaihe ${progress.step}/${progress.totalSteps}`
                    : ""}
                </span>
              </div>
              {!terminalState && (
                <Progress value={progressPercent} className="h-1.5" />
              )}
              {progress?.detail && !terminalState && (
                <p className="text-xs text-muted-foreground">
                  {progress.detail}
                </p>
              )}
              {(submitError || workerError) && (
                <p className="text-xs text-destructive break-words whitespace-pre-wrap">
                  {submitError || workerError}
                </p>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                {isPolling && !isComplete ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleStop}
                    className="h-8 px-3 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                  >
                    <UncheckedIcon className="h-3 w-3 mr-1.5 fill-current" />
                    Pysäytä
                  </Button>
                ) : terminalState ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenChange(false)}
                  >
                    Sulje
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {!canSubmit && hasAnyNewItems && missingResponsibleCount > 0 && (
                <div className="flex items-center gap-2 text-xs text-destructive flex-1">
                  <AlertIcon className="h-3.5 w-3.5 shrink-0" />
                  {missingResponsibleCount === 1
                    ? `1/${newPPCount} uudelta hankintapaketilta puuttuu vastuuhenkilö`
                    : `${missingResponsibleCount}/${newPPCount} uudelta hankintapaketilta puuttuu vastuuhenkilö`}
                </div>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  Peruuta
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || loading}
                >
                  Suorita
                </Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog
      open={showDeletionConfirm}
      onOpenChange={setShowDeletionConfirm}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Hyväksymättömiä poistoja</AlertDialogTitle>
          <AlertDialogDescription>
            Oletko varma että haluat suorittaa synkronoinnin ilman, että
            hyväksyt joitain poistoja? Hyväksymättömät poistot jätetään
            ennalleen ja ne tulevat näkyviin uudelleen seuraavassa
            synkronoinnissa.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Peruuta</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              setShowDeletionConfirm(false);
              void performSubmit();
            }}
          >
            Suorita silti
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}